/**
 * Medallion Customizer V3.2
 * Robust Error Handling Fix
 */

class MedallionCustomizer {
  constructor() {
    this.container = document.querySelector('.medallion-customizer');
    if (!this.container) return;

    this.basePrice = parseInt(this.container.dataset.basePrice || 0);
    this.dateCharge = 200; 
    this.presetBacksideCharge = 300; 
    this.customBacksideCharge = 1000; 
    
    this.init();
  }

  init() {
    try {
      this.cacheElements();
      this.attachEventListeners();
      this.initCharCounters();
      
      // Initialize visibility based on current dropdown values
      // This ensures fields display correctly when returning from cart
      this.toggleDateInput();
      this.handleBacksideChange();
      
      this.updatePriceDisplay();
      this.attachFormValidation();
      
      // Run calculation immediately in case value exists
      if (this.sobrietyDate) {
        this.calculateMilestone();
      }
    } catch (error) {
      console.error('Medallion Customizer Error:', error);
    }
  }

  cacheElements() {
    // Helper to safely find elements
    const find = (selector) => this.container.querySelector(selector);
    const findAll = (selector) => this.container.querySelectorAll(selector);
    const getById = (id) => document.getElementById(id);

    // Input fields
    this.recoveryProgram = find('.medallion-recovery-program');
    this.dateEngraving = find('.medallion-date-engraving');
    this.dateValue = find('.medallion-date-value');
    this.sobrietyDate = find('.medallion-sobriety-date');
    this.backsideDesign = find('.medallion-backside-design');
    this.customMessage = find('.medallion-custom-message');
    this.fontRadios = findAll('.font-radio');

    // Conditional wrappers - SAFE ACCESS
    if (this.dateValue && this.dateValue.id) {
      this.dateInputWrapper = getById(this.dateValue.id.replace('date-value', 'date-input-wrapper'));
    }
    
    if (this.customMessage && this.customMessage.id) {
      this.customMessageWrapper = getById(this.customMessage.id.replace('custom-message', 'custom-message-wrapper'));
    }

    if (this.backsideDesign && this.backsideDesign.id) {
      this.backsidePreview = getById(this.backsideDesign.id.replace('backside-design', 'backside-preview'));
      this.backsidePreviewImg = getById(this.backsideDesign.id.replace('backside-design', 'backside-preview-img'));
    }
    
    // Milestone display
    this.milestoneDisplay = find('.medallion-customizer__milestone-display');
    this.milestoneText = this.milestoneDisplay ? this.milestoneDisplay.querySelector('strong') : null;
    this.milestoneHidden = find('input[name="properties[Sobriety Milestone]"]');

    // Price display elements
    this.totalPriceElement = find('.medallion-customizer__total .medallion-customizer__price-value');
    
    // Safe addon finding
    if (this.dateEngraving && this.dateEngraving.id) {
      const dateAddonId = this.dateEngraving.id.replace('date-engraving', 'date-addon');
      this.dateAddonElement = getById(dateAddonId);
    }

    if (this.backsideDesign && this.backsideDesign.id) {
      const backsideAddonId = this.backsideDesign.id.replace('backside-design', 'backside-addon');
      this.backsideAddonElement = getById(backsideAddonId);
      
      if (this.backsideAddonElement) {
        const backsideLabelId = this.backsideDesign.id.replace('backside-design', 'backside-addon-label');
        const backsidePriceId = this.backsideDesign.id.replace('backside-design', 'backside-addon-price');
        this.backsideAddonLabel = getById(backsideLabelId);
        this.backsideAddonPrice = getById(backsidePriceId);
      }
    }

    // Form
    this.form = this.sobrietyDate ? this.sobrietyDate.closest('form') : null;
    this.submitButton = this.form ? this.form.querySelector('[type="submit"]') : null;

    // Global error
    this.globalError = find('.medallion-customizer__global-error');
  }

  attachEventListeners() {
    // Date engraving dropdown
    if (this.dateEngraving) {
      this.dateEngraving.addEventListener('change', () => {
        this.toggleDateInput();
        this.updatePriceDisplay();
        this.selectVariant(); // Update variant when date option changes
      });
    }

    // Sobriety date - MULTIPLE EVENTS for robustness
    if (this.sobrietyDate) {
      ['change', 'input', 'blur'].forEach(event => {
        this.sobrietyDate.addEventListener(event, () => {
          this.calculateMilestone();
        });
      });
    }

    // Backside design dropdown
    if (this.backsideDesign) {
      this.backsideDesign.addEventListener('change', () => {
        this.handleBacksideChange();
        this.selectVariant(); // Update variant when backside changes
      });
    }
  }

  selectVariant() {
    // Find the variant that matches the selected backside + date option
    if (!this.backsideDesign || !this.dateEngraving || !this.form) return;
    
    const backsideValue = this.backsideDesign.value;
    const dateOption = this.dateEngraving.value;
    
    // Don't try to select variant if placeholder is still selected
    if (!backsideValue || backsideValue === '') return;
    
    console.log('[DEBUG] ===== Variant Selection Started =====');
    console.log('[DEBUG] Selected Backside:', backsideValue);
    console.log('[DEBUG] Selected Date Option:', dateOption);
    
    // Method 1: Update Shopify's option selectors directly
    const option1Select = this.form.querySelector('select[name="options[Backside Design]"]');
    const option2Select = this.form.querySelector('select[name="options[Personalized Sobriety Date]"]');
    
    if (option1Select && option2Select) {
      console.log('[DEBUG] Found Shopify option selectors');
      console.log('[DEBUG] Option1 current value:', option1Select.value);
      console.log('[DEBUG] Option2 current value:', option2Select.value);
      
      // Update option 1 (Backside Design)
      option1Select.value = backsideValue;
      console.log('[DEBUG] Set Option1 to:', backsideValue);
      console.log('[DEBUG] Option1 value after set:', option1Select.value);
      
      // Update option 2 (Date)
      option2Select.value = dateOption;
      console.log('[DEBUG] Set Option2 to:', dateOption);
      console.log('[DEBUG] Option2 value after set:', option2Select.value);
      
      // Trigger change event on option1 first
      const changeEvent1 = new Event('change', { bubbles: true });
      option1Select.dispatchEvent(changeEvent1);
      console.log('[DEBUG] Dispatched change event on Option1');
      
      // Small delay before updating option2 to ensure Shopify processes option1
      setTimeout(() => {
        const changeEvent2 = new Event('change', { bubbles: true });
        option2Select.dispatchEvent(changeEvent2);
        console.log('[DEBUG] Dispatched change event on Option2');
      }, 50);
      
      return;
    }
    
    console.warn('[DEBUG] Could not find Shopify option selectors');
    console.log('[DEBUG] Available select elements:', Array.from(this.form.querySelectorAll('select')).map(s => s.name));
  }

  calculateMilestone() {
    if (!this.milestoneDisplay || !this.milestoneText || !this.sobrietyDate) return;

    const dateValue = this.sobrietyDate.value;
    if (!dateValue) {
      this.milestoneDisplay.style.display = 'none';
      return;
    }

    // Force date parsing to handle YYYY-MM-DD standard from HTML5 input
    const [year, month, day] = dateValue.split('-').map(num => parseInt(num, 10));
    
    // Create date at midnight local time to avoid timezone issues
    const sobrietyDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Compare vs today midnight

    if (isNaN(sobrietyDate.getTime())) {
      this.milestoneDisplay.style.display = 'none';
      return;
    }
    
    // Simple year subtraction for medallion milestone (shows anniversary year being celebrated)
    const years = today.getFullYear() - sobrietyDate.getFullYear();
    
    let milestoneText = '';

    if (years < 1) {
      // Less than 1 year - show months or days
      const oneDay = 24 * 60 * 60 * 1000;
      const diffDays = Math.round((today - sobrietyDate) / oneDay);
      const totalMonths = Math.floor(diffDays / 30);
      
      if (diffDays < 0) {
        milestoneText = 'Invalid Date';
      } else if (totalMonths < 1) {
        milestoneText = diffDays <= 0 ? 'Less than 1 Day' : (diffDays === 1 ? '1 Day' : `${diffDays} Days`);
      } else {
        milestoneText = totalMonths === 1 ? '1 Month' : `${totalMonths} Months`;
      }
    } else {
      milestoneText = years === 1 ? '1 Year' : `${years} Years`;
    }

    this.milestoneText.textContent = milestoneText;
    if (this.milestoneHidden) this.milestoneHidden.value = milestoneText;
    this.milestoneDisplay.style.display = 'flex';
  }

  toggleDateInput() {
    if (!this.dateEngraving) return;

    const selectedOption = this.dateEngraving.options[this.dateEngraving.selectedIndex];
    const isWithDate = selectedOption.value === 'With Sobriety Date' || selectedOption.value === 'With Date';
    
    // Sobriety date field is always required and visible
    // Variant selection handles whether the date is actually engraved
  }

  handleBacksideChange() {
    if (!this.backsideDesign) return;

    const selectedOption = this.backsideDesign.options[this.backsideDesign.selectedIndex];
    const isCustomMessage = selectedOption.value === 'Custom Message';
    const imageUrl = selectedOption.dataset.image;

    // Toggle preview image
    if (this.backsidePreview && this.backsidePreviewImg) {
      if (imageUrl && !isCustomMessage) {
        this.backsidePreviewImg.src = imageUrl;
        this.backsidePreview.style.display = 'block';
      } else {
        this.backsidePreview.style.display = 'none';
        this.backsidePreviewImg.src = '';
      }
    }
    
    // Toggle custom message fields
    if (this.customMessageWrapper && this.customMessage) {
      if (isCustomMessage) {
        this.customMessageWrapper.style.display = 'block';
        this.customMessage.setAttribute('required', 'required');
        if (this.fontRadios.length > 0) {
          this.fontRadios[0].setAttribute('required', 'required');
        }
        
        // Scroll to font selection after user enters message
        // Listen for input to trigger scroll
        this.customMessage.addEventListener('input', () => {
          if (this.customMessage.value.length > 10) {
            setTimeout(() => {
              const fontOptions = this.customMessageWrapper.querySelector('.medallion-font-options');
              if (fontOptions) {
                fontOptions.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 800);
          }
        }, { once: true }); // Only trigger once per selection
        
      } else {
        this.customMessageWrapper.style.display = 'none';
        this.customMessage.removeAttribute('required');
        this.customMessage.value = '';
        this.fontRadios.forEach(radio => {
          radio.removeAttribute('required');
          radio.checked = false;
        });
        this.clearError(this.customMessage);
        // Update character counter
        const counter = document.querySelector(`[data-for="${this.customMessage.id}"]`);
        if (counter) counter.textContent = '0';
      }
    }

    this.updatePriceDisplay();
  }

  initCharCounters() {
    if (this.customMessage) {
      this.customMessage.addEventListener('input', (e) => {
        const counter = document.querySelector(`[data-for="${this.customMessage.id}"]`);
        if (counter) {
          counter.textContent = e.target.value.length;
        }
      });
    }
  }

  updatePriceDisplay() {
    if (!this.totalPriceElement) return;
    
    // Get price from selected variant instead of calculating manually
    const variantSelector = this.form ? this.form.querySelector('select[name="id"]') : null;
    let variantPrice = this.basePrice;
    
    if (variantSelector && variantSelector.selectedOptions.length > 0) {
      const selectedOption = variantSelector.selectedOptions[0];
      const priceData = selectedOption.dataset.price || selectedOption.getAttribute('data-price');
      if (priceData) {
        variantPrice = parseInt(priceData);
      }
    }

    // Update addon display based on backside selection
    if (this.backsideDesign && this.backsideAddonElement) {
      const selectedOption = this.backsideDesign.options[this.backsideDesign.selectedIndex];
      if (selectedOption && selectedOption.value && selectedOption.value !== '') {
        const priceModifier = parseInt(selectedOption.dataset.price || 0);
        
        if (priceModifier > 0) {
          if (this.backsideAddonLabel && this.backsideAddonPrice) {
            if (selectedOption.value === 'Custom Message') {
              this.backsideAddonLabel.textContent = 'Custom Back Design:';
              this.backsideAddonPrice.textContent = '+$10.00';
            } else {
              this.backsideAddonLabel.textContent = 'Backside Design:';
              this.backsideAddonPrice.textContent = '+$3.00';
            }
          }
          this.backsideAddonElement.style.display = 'flex';
        } else {
          this.backsideAddonElement.style.display = 'none';
        }
      } else {
        this.backsideAddonElement.style.display = 'none';
      }
    }

    this.totalPriceElement.textContent = this.formatMoney(variantPrice);
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  attachFormValidation() {
    if (!this.form) return;

    this.form.addEventListener('submit', (e) => {
      if (!this.validateForm()) {
        e.preventDefault();
        e.stopPropagation();
        this.showGlobalError('Please fix the errors above before adding to cart.');
        
        const firstError = this.container.querySelector('.medallion-customizer__error:not(:empty)');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });
  }

  validateForm() {
    let isValid = true;
    this.clearGlobalError();

    if (this.recoveryProgram && !this.recoveryProgram.value) {
      this.showError(this.recoveryProgram, 'Recovery program is required');
      isValid = false;
    }

    if (this.dateEngraving && this.dateValue) {
      const dateOption = this.dateEngraving.options[this.dateEngraving.selectedIndex];
      if (dateOption.value === 'With Date') {
        if (!this.dateValue.value) {
          this.showError(this.dateValue, 'Date is required when date engraving is selected');
          isValid = false;
        } else {
          this.clearError(this.dateValue);
        }
      }
    }

    if (this.sobrietyDate) {
      if (!this.sobrietyDate.value) {
        this.showError(this.sobrietyDate, 'Sobriety date is required');
        isValid = false;
      } else {
        this.clearError(this.sobrietyDate);
      }
    }

    if (this.backsideDesign && this.customMessage) {
      const selectedOption = this.backsideDesign.options[this.backsideDesign.selectedIndex];
      if (selectedOption.value === 'Custom Message') {
        if (!this.customMessage.value.trim()) {
          this.showError(this.customMessage, 'Custom message is required');
          isValid = false;
        } else if (this.customMessage.value.length > 200) {
          this.showError(this.customMessage, 'Custom message must be 200 characters or less');
          isValid = false;
        } else {
          this.clearError(this.customMessage);
        }

        const selectedFont = Array.from(this.fontRadios).find(radio => radio.checked);
        if (!selectedFont) {
          this.showError(this.customMessage, 'Please select a font for your custom message');
          isValid = false;
        }
      }
    }

    return isValid;
  }

  showError(field, message) {
    const errorElement = document.querySelector(`[data-error-for="${field.id}"]`);
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = 'block';
    }
    field.classList.add('field--error');
  }

  clearError(field) {
    const errorElement = document.querySelector(`[data-error-for="${field.id}"]`);
    if (errorElement) {
      errorElement.textContent = '';
      errorElement.style.display = 'none';
    }
    field.classList.remove('field--error');
  }

  showGlobalError(message) {
    if (this.globalError) {
      const messageElement = this.globalError.querySelector('.error-message');
      if (messageElement) {
        messageElement.textContent = message;
      }
      this.globalError.style.display = 'block';
    }
  }

  clearGlobalError() {
    if (this.globalError) {
      this.globalError.style.display = 'none';
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MedallionCustomizer();
  });
} else {
  new MedallionCustomizer();
}
